import Random from "rung/src/random";

const rng = new Random(Math.random);

function logEvent(message) {
  document.dispatchEvent(new CustomEvent("game.event", {
    detail: {
      message,
    }
  }));
}

// Quick function to average out damage to a triangular sawtooth distribution
// TODO: document and add this concept to Rung
function rollDamage(min, max) {
  const d1 = rng.integer(min, max + 1);
  const d2 = rng.integer(min, max + 1);
  const average = (d1 + d2) / 2;

  if (d1 < d2) {
    return Math.floor(average);
  } else if (d1 > d2) {
    return Math.ceil(average);
  } else {
    return average;
  }
}

class Entity {
  constructor(x, y, glyph, name, components) {
    this.x = x;
    this.y = y;
    this.glyph = glyph;
    this.name = name;
    this.action = null;
    this.components = Object.keys(components);

    for (let attribute of this.components) {
      this[attribute] = components[attribute];
      this[attribute].owner = this;
    }
  }

  consumeShot() {
    // NPCs have unlimited ammo for now
    if (this.isPlayer()) {
      return this.equipment.consumeShot();
    }
  }

  isPlayer() {
    return false;
  }

  isItem() {
    return this.hasOwnProperty("item");
  }

  isWeapon() {
    return this.hasOwnProperty("weapon");
  }

  isArmor() {
    return this.hasOwnProperty("armor");
  }

  isAmmunition() {
    return this.hasOwnProperty("ammunition");
  }

  isReflective() {
    return this.name == "Hand Mirror";
  }

  isBlocking() {
    return this.hasOwnProperty("mass");
  }

  isSensate() {
    return this.hasOwnProperty("sense");
  }

  get meleeAttackModifier() {
    return this.stats.strengthModifier;
  }

  get rangedAttackModifier() {
    return this.stats.agilityModifier;
  }

  get armorBonus() {
    if (this.equipment) {
      return this.equipment.armor ? this.equipment.armor.bonus : 0;
    } else {
      // TODO: add scaly hide and other animal defences
      return 0;
    }
  }

  get defenceScore() {
    return 5 + this.stats.agilityModifier + this.armorBonus;
  }

  get meleeAttackDamage() {
    return this.equipment ? this.equipment.activeWeaponDamage : this.naturalAttack.damage;
  }

  isEnemy() {
    if (this.isSensate()) {
      return this.sense.isEnemy();
    }
    return false;
  }

  // TODO: separate into Actor, Player and Item subclasses
  bumpFrom(entity, stage) {
    let wantsToMelee = false;

    if (this.isSensate()) {
      wantsToMelee = this.sense.bumpFrom(entity);
    } else if (this.isPlayer()) {
      wantsToMelee = entity.isEnemy();
    }

    if (wantsToMelee) {
      this.hitFrom(entity);
    } else {
      this.shovedFrom(entity, stage);
    }
  }

  shovedFrom(entity, stage) {
    const mx = entity.x;
    const my = entity.y;
    const nx = this.x;
    const ny = this.y;
    stage.moveEntityTo(entity, nx, ny);
    stage.moveEntityTo(this, mx, my);
    stage.refreshVisibility();
  }

  hitFrom(entity) {
    const attackRoll = entity.meleeAttackModifier + rng.integer(1, 21);

    if (attackRoll >= this.defenceScore) {
      const hit = rollDamage(1, entity.meleeAttackDamage);
      this.health.hp -= hit;
      logEvent(`${entity.name} hits ${this.name} for ${hit}`);
    } else {
      logEvent(`${this.name} dodges attack from ${entity.name}`);
    }

    if (this.health.hp <= 0) {
      // Kill player and trigger UI event.
      if (this.isPlayer()) {
        const attack = entity.equipment ? entity.equipment.weapon.name : entity.naturalAttack.name;

        document.dispatchEvent(new CustomEvent("ui.transition", {
          detail: {
            command: "DeathScreen",
            message: `The ${entity.name} killed you with a ${attack}.`
          }
        }));
      } else {
        logEvent(`${this.name} was defeated`);
      }
    }
  }

  shotFrom(entity) {
    const attackRoll = entity.rangedAttackModifier + rng.integer(1, 21);
    const shot = entity.equipment.consumeShot();

    if (attackRoll >= this.defenceScore) {

      if (this.isSensate()) {
        this.sense.shotFrom(entity);
      }

      const hit = rollDamage(1, shot.damage);
      this.health.hp -= hit;
      logEvent(`${this.name} shot by ${entity.name} for ${hit}`);
    } else {
      logEvent(`${this.name} dodged shot from ${entity.name}`);
    }

    if (this.health.hp <= 0) {
      // Kill player and trigger UI event.
      if (this.isPlayer()) {
        document.dispatchEvent(new CustomEvent("ui.transition", {
          detail: {
            command: "DeathScreen",
            message: `Shot with ${shot.ammunition.toLowerCase()} by a ${entity.name}.`
          }
        }));
      } else {
        logEvent(`${this.name} was defeated`);
      }
    }
  }

  needsInput() {
    return false;
  }

  actionDelay() {
    return this.speed.actionDelay();
  }

  isVulnerable() {
    return this.hasOwnProperty("health");
  }

  hit(damage) {
    this.health.hp -= damage;
  }

  canTakeTurn() {
    return this.hasOwnProperty("sense");
  }

  takeTurn(stage) {
    if (!this.health.isDead()) {
      this.sense.takeTurn(stage);
    }
    return !this.health.isDead();
  }
}

export default Entity;
